import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { acceptBaseLeagueRules } from '../../lib/baseLeagueRulesAcceptance';
import { RichTextContent } from '../shared/RichTextEditor/RichTextContent';

interface Props {
  userId: number;
  league: { id: number; name: string };
  onAccepted: () => void;
}

/** Page exclusivement montée par BaseStandardBuildGuard, jamais par les embeds. */
export function BaseLeagueRulesOnboarding({ userId, league, onAccepted }: Props) {
  const [rules, setRules] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [checked, setChecked] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    setLoading(true);
    setError(false);
    setChecked(false);

    async function loadRules() {
      try {
        const { data, error: queryError } = await supabase
          .from('league_settings')
          .select('speceficruleexplication')
          .eq('league_id', league.id)
          .abortSignal(controller.signal)
          .single();
        if (queryError || !data) throw queryError ?? new Error('Paramètres absents');
        if (!cancelled) setRules(data.speceficruleexplication ?? '');
      } catch {
        if (!cancelled) setError(true);
      } finally {
        window.clearTimeout(timeout);
        if (!cancelled) setLoading(false);
      }
    }

    void loadRules();
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [league.id, userId, attempt]);

  const confirm = () => {
    if (!checked || loading || error) return;
    acceptBaseLeagueRules(userId, league.id);
    onAccepted();
  };

  return (
    <section className="card shadow-lg border-0 mx-auto" style={{ maxWidth: 800 }} aria-labelledby="base-rules-title">
      <div className="card-body p-3 p-md-4">
        <Link to="/select-league" className="btn btn-secondary mb-4">Retour aux ligues</Link>
        <p className="text-muted mb-1">{league.name}</p>
        <h1 id="base-rules-title" className="h3 fw-bold">Règlements de la ligue</h1>
        <p className="text-muted mb-4">Prenez connaissance des règlements avant de construire votre équipe.</p>

        <div className="mb-4" aria-busy={loading}>
          {loading ? (
            <div className="text-center py-4" role="status">
              <span className="spinner-border text-primary" aria-hidden="true" />
              <p className="mt-2 mb-0">Chargement des règlements…</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger" role="alert">
              <p>Impossible de charger les règlements. Veuillez réessayer.</p>
              <button type="button" className="btn btn-outline-danger" onClick={() => setAttempt(a => a + 1)}>Réessayer</button>
            </div>
          ) : (
            <RichTextContent html={rules} emptyFallback={
              <p className="text-muted">Aucun règlement spécifique n’a encore été saisi par le commissaire.</p>
            } />
          )}
        </div>

        <div className="form-check mb-4">
          <input id="base-rules-acceptance" className="form-check-input" type="checkbox"
            checked={checked} disabled={loading || error} onChange={e => setChecked(e.target.checked)} />
          <label className="form-check-label" htmlFor="base-rules-acceptance">J’ai lu et j’accepte les règlements de la ligue</label>
        </div>
        <button type="button" className="btn btn-primary w-100" disabled={!checked || loading || error} onClick={confirm}>
          Construire mon équipe
        </button>
      </div>
    </section>
  );
}
